<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, toRaw, watch } from 'vue';
import { CardInputSchema, type CardInput, type CardState, type CardUse } from '@vp/shared-types';
import { cardViolation, fromSandbox, moduleHasProfileStats, profileCardInput, sandboxUnsafe, sendProfileCardMessage } from '../modules';
import { cardState, mergeCardState } from '../profile-looks';
import { sandboxAttrs } from '../editor/sandbox';
import { goToBookmark, safeUrl } from '../editor/bookmarks';
import { ask } from '../dialogs';
import { locale, t } from '../i18n';
import { logger } from '../platform';
import { useApp } from '../stores/app';
import { canvasDark } from '../theme';

/**
 * A profile card on the platform's card page (public/profile-card.html, module API 4.10): HTML and CSS
 * without scripts; the page's bridge wires its `data-vp-*` elements and live data.
 * - A module's card (`moduleId`): its state is the profile's, the module hears every use and gets
 *   `{type:'opened'}` / `{type:'closed'}`; counters of the profile only with `profile.stats`.
 * - The person's card (`user:<id>`): the same, with no module; counters of the profile always.
 * - The card editor (`localState`): the state is the editor's, uses come back as `input` events.
 * While the tray is edited (`editing`) the card does not take the pointer.
 */
const props = defineProps<{
  html: string;
  uses: readonly CardUse[];
  title: string;
  stats: Record<string, number>;
  editing?: boolean;
  /** The card on the tray (`<moduleId>:<card>` or `user:<id>`); absent in the editor. */
  panelId?: string;
  moduleId?: string;
  card?: string;
  /** The editor's own state (no profile, no module). */
  localState?: CardState;
}>();
const emit = defineEmits<{ input: [input: CardInput] }>();

const app = useApp();
const frame = ref<HTMLIFrameElement | null>(null);
const PAGE = `${import.meta.env.BASE_URL}profile-card.html`;
let loads = 0;
const src = ref('');
let opened = false;
const attrs = sandboxAttrs({ html: '' });

const state = computed<CardState>(() => (props.localState ? props.localState : props.panelId ? cardState(props.panelId) : {}));
const stats = computed(() => (props.moduleId && !moduleHasProfileStats(props.moduleId) ? {} : toRaw(props.stats)));

function reload(): void {
  opened = false;
  src.value = `${PAGE}?d=${++loads}`;
}

function post(data: Record<string, unknown>): void {
  frame.value?.contentWindow?.postMessage({ vpCard: 1, ...data }, '*');
}

/** The document (again): a new HTML is written in place, without loading the page afresh. */
function sendDoc(): void {
  post({ type: 'doc', html: props.html, uses: [...props.uses], state: { ...toRaw(state.value) }, stats: { ...stats.value }, locale: locale.value });
}

async function follow(target: string): Promise<void> {
  const i = target.indexOf(':');
  const kind = target.slice(0, i);
  const arg = target.slice(i + 1);
  if (kind === 'canvas' && app.canvases.some((c) => c.id === arg)) await app.openCanvas(arg);
  else if (kind === 'bookmark') {
    const [canvasId, bookmarkId] = arg.split('/');
    if (!app.canvases.some((c) => c.id === canvasId)) return;
    await app.openCanvas(canvasId);
    setTimeout(() => goToBookmark(bookmarkId ?? ''), 300);
  } else if (kind === 'https') {
    const url = safeUrl(target);
    if (url?.startsWith('https:') && (await ask(t('profile.cardLinkAsk', { url }), { okLabel: t('profile.cardLinkOpen') }))) window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function onMessage(e: MessageEvent): void {
  if (!frame.value || e.source !== frame.value.contentWindow) return;
  const d = e.data as { vpCard?: unknown; written?: unknown; input?: unknown; link?: unknown; csp?: unknown } | null;
  if (!d || d.vpCard !== 1) return;
  if (typeof d.written === 'boolean') {
    if (!d.written) {
      if (props.moduleId) sandboxUnsafe(props.moduleId);
      else logger.error('profile.card.unsafe', {});
    } else if (!opened && props.moduleId && props.card) {
      opened = true;
      sendProfileCardMessage(props.moduleId, props.card, { type: 'opened' });
    }
    return;
  }
  if (typeof d.csp === 'string') {
    if (props.moduleId) cardViolation(props.moduleId, d.csp.slice(0, 40));
    return;
  }
  if (props.editing || (props.moduleId && !fromSandbox(props.moduleId, d))) return;
  if (typeof d.link === 'string') {
    // the editor only shows where a link leads; the profile follows it
    if (!props.localState) void follow(d.link.slice(0, 2000));
    return;
  }
  const input = CardInputSchema.safeParse(d.input);
  if (!input.success) return;
  if (props.localState) {
    emit('input', input.data);
    return;
  }
  if ('key' in input.data && props.panelId && !mergeCardState(props.panelId, { [input.data.key]: input.data.value })) return;
  if (props.moduleId && props.card) profileCardInput(props.moduleId, props.card, input.data);
}

onMounted(() => {
  window.addEventListener('message', onMessage);
  reload();
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  if (opened && props.moduleId && props.card) sendProfileCardMessage(props.moduleId, props.card, { type: 'closed' });
});
watch(() => [props.html, props.uses.join(',')], sendDoc);
watch(() => JSON.stringify(state.value), () => post({ type: 'state', state: { ...toRaw(state.value) } }));
watch(() => JSON.stringify(stats.value), () => post({ type: 'stats', stats: { ...stats.value } }));
watch(locale, sendDoc);
</script>

<template>
  <iframe
    v-if="src"
    ref="frame"
    class="profile-card-frame"
    :src="src"
    sandbox="allow-scripts"
    :allow="attrs.allow"
    :data-profile-card="panelId"
    :data-module="moduleId"
    referrerpolicy="no-referrer"
    :title="title"
    :style="{ pointerEvents: editing ? 'none' : 'auto', colorScheme: canvasDark ? 'dark' : 'light' }"
    @load="sendDoc"
  ></iframe>
</template>

<style scoped>
.profile-card-frame {
  display: block;
  flex: 1;
  min-height: 0;
  width: 100%;
  border: none;
  background: transparent;
}
</style>
